import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Ticket, Coffee, Crown } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function CustomerRewards() {
  const [progress, setProgress] = useState(null);
  const [ents, setEnts] = useState([]);
  const [tiers, setTiers] = useState([]);
  const load = () => {
    api.get("/customer/progress").then((r) => setProgress(r.data)).catch(() => setProgress({ stamp_cards: [], milestones: [], streaks: [] }));
    api.get("/customer/entitlements").then((r) => setEnts(r.data)).catch(() => setEnts([]));
    api.get("/customer/memberships").then((r) => setTiers(r.data)).catch(() => setTiers([]));
  };
  useEffect(() => { load(); }, []);

  const redeem = async (id) => {
    try { await api.post(`/customer/entitlements/${id}/redeem`); toast.success("Reward redeemed — show staff the confirmation"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!progress) return <div className="text-muted-foreground pt-6">Loading…</div>;
  const available = ents.filter((e) => e.status === "AVAILABLE");

  return (
    <div className="space-y-6 pt-2" data-testid="customer-rewards">
      <h1 className="font-display font-bold text-2xl">Rewards</h1>

      {tiers.length > 0 && (
        <div data-testid="memberships">
          <p className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><Crown size={16} className="text-primary" /> Memberships</p>
          <div className="space-y-2">
            {tiers.map((t, i) => (
              <div key={i} data-testid="tier-badge" className="glass rounded-2xl p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0"><Crown size={20} weight="duotone" className="text-primary" /></div>
                <div className="min-w-0 flex-1"><p className="font-display font-semibold text-sm truncate">{t.tier_name} · {t.business}</p><p className="text-[11px] text-muted-foreground">{t.benefits || `×${t.multiplier} earning`}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><Gift size={16} className="text-primary" /> Available rewards</p>
        {available.length === 0 ? <p className="text-sm text-muted-foreground">No rewards to redeem yet — keep earning!</p> : (
          <div className="space-y-2">
            {available.map((e) => (
              <div key={e.id} data-testid={`entitlement-${e.id}`} className="glass rounded-2xl p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0"><Ticket size={20} weight="duotone" className="text-primary" /></div>
                <div className="min-w-0 flex-1"><p className="font-display font-semibold text-sm truncate">{e.title}</p><p className="text-[11px] text-muted-foreground">{e.business}{e.valid_until ? ` · valid to ${e.valid_until.slice(0, 10)}` : ""}</p></div>
                <Button data-testid={`redeem-${e.id}`} onClick={() => redeem(e.id)} className="rounded-full h-9 bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold shrink-0">Redeem</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><Coffee size={16} className="text-primary" /> Stamp cards</p>
        {progress.stamp_cards.length === 0 ? <p className="text-sm text-muted-foreground">No stamp cards yet.</p> : (
          <div className="space-y-2">
            {progress.stamp_cards.map((s, i) => (
              <div key={i} data-testid="stamp-card" className="glass rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2"><p className="font-display font-semibold text-sm">{s.program} <span className="text-xs text-muted-foreground">· {s.business}</span></p><span className="text-xs text-muted-foreground">{s.count} of {s.required}</span></div>
                <div className="flex gap-1.5">{Array.from({ length: s.required }).map((_, k) => <div key={k} className={`h-2.5 flex-1 rounded-full ${k < s.count ? "bg-primary" : "bg-black/10"}`} />)}</div>
                <p className="text-[11px] text-muted-foreground mt-1.5">{s.required - s.count} more to a free reward</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {(progress.milestones.length > 0 || progress.streaks.length > 0) && (
        <div>
          <p className="text-sm font-semibold text-muted-foreground mb-2">Your progress</p>
          <div className="space-y-2">
            {progress.milestones.map((m, i) => <div key={`m${i}`} className="glass rounded-2xl p-4 text-sm flex justify-between"><span>{m.name} · {m.business}</span><span className="text-muted-foreground">{m.progress}/{m.threshold}</span></div>)}
            {progress.streaks.map((s, i) => <div key={`s${i}`} className="glass rounded-2xl p-4 text-sm flex justify-between"><span>{s.name} · {s.business}</span><span className="text-muted-foreground">{s.current}/{s.required} weeks</span></div>)}
          </div>
        </div>
      )}
    </div>
  );
}
